import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, Sparkles, Home as HomeIcon, ShieldCheck,
  CheckCircle2, ChevronLeft, ChevronRight, Pause, Play, Stethoscope, Heart,
  Users, BedDouble, Bath, Star, ClipboardCheck, FileCheck2, Activity, Award,
} from "lucide-react";
import SmoothImage from "./SmoothImage";
import { trackPhoneClick } from "../lib/tracking";
import { IMG } from "../data/images";

const SLIDE_DURATION = 6200;

// ---------- Slide stages ----------

function useTilt(strength = 12) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / r.width;
      const dy = (e.clientY - cy) / r.height;
      el.style.transform = `perspective(1100px) rotateX(${(-dy * strength).toFixed(2)}deg) rotateY(${(dx * strength).toFixed(2)}deg) translateZ(0)`;
    };
    const onLeave = () => { el.style.transform = "perspective(1100px) rotateX(0) rotateY(0)"; };
    window.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [strength]);
  return ref;
}

function WarmStage() {
  const t1 = useTilt(8);
  const t2 = useTilt(8);
  const t3 = useTilt(8);
  return (
    <div className="absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-3 z-30" style={{ perspective: "1200px" }}>
      <div ref={t1} className="alpha-tilt alpha-glass-dark border border-white/15 rounded-2xl p-4 max-w-[260px] shadow-2xl alpha-float-a">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-[color:var(--alpha-green-600)]/85 text-white flex items-center justify-center"><Heart className="w-4 h-4" /></span>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/65 font-semibold">Person-centred</div>
        </div>
        <div className="mt-2 font-display text-[15px] font-semibold !text-white leading-tight">Matched on values, not just availability.</div>
      </div>
      <div ref={t2} className="alpha-tilt alpha-glass-dark border border-white/15 rounded-2xl p-4 max-w-[260px] shadow-2xl ml-8 alpha-float-b">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-[color:var(--alpha-purple-600)] text-white flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></span>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/65 font-semibold">Intake replies in</div>
        </div>
        <div className="mt-2 font-display text-[20px] font-semibold !text-white leading-tight">1 business day</div>
        <div className="text-[11px] text-white/65 mt-0.5">VIC · WA hubs · National</div>
      </div>
      <div ref={t3} className="alpha-tilt alpha-glass-dark border border-white/15 rounded-2xl p-3 max-w-[260px] shadow-2xl alpha-float-c">
        <div className="flex items-center gap-2 text-[12px] text-white/85 font-medium">
          <CheckCircle2 className="w-4 h-4 text-[color:var(--alpha-green-500)]" /> Worker-screened, trauma-informed
        </div>
      </div>
    </div>
  );
}

function HomesStage() {
  const t = useTilt(10);
  return (
    <div className="absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 hidden md:block z-30 alpha-float-a" style={{ perspective: "1200px" }}>
      <div ref={t} className="alpha-tilt rounded-3xl overflow-hidden alpha-ring-gradient shadow-2xl w-[300px] lg:w-[340px] bg-white">
        <div className="relative aspect-[4/3]">
          <SmoothImage src="/homes/coburg/4.jpg" alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1B0B3D]/55 to-transparent" />
          <span className="absolute top-3 left-3 alpha-chip !bg-[color:var(--alpha-green-600)] !text-white !border-[color:var(--alpha-green-700)] !py-1 !px-2.5 !text-[10.5px] font-semibold uppercase tracking-[0.18em]">Available now</span>
          <span className="absolute top-3 right-3 alpha-chip !bg-white !text-[color:var(--alpha-purple-700)] !border-white !py-1 !px-2.5 !text-[11px] font-semibold">1:1 & 1:2</span>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[color:var(--alpha-mute)] font-semibold">Featured · SIL Penthouse</div>
          <div className="mt-1 font-display font-semibold text-[18px] leading-tight">The Coburg Penthouse</div>
          <div className="mt-2 flex items-center gap-3 text-xs text-[color:var(--alpha-mute)]">
            <span className="inline-flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> Multi-bed</span>
            <span className="inline-flex items-center gap-1"><Bath className="w-3.5 h-3.5" /> Spa bath</span>
            <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)] fill-current" /> Premium</span>
          </div>
        </div>
      </div>
      <div className="alpha-glass-dark border border-white/15 rounded-2xl p-3 mt-3 w-[300px] lg:w-[340px] shadow-2xl alpha-float-b">
        <div className="flex items-center gap-2 text-[12px] text-white/85 font-medium">
          <Sparkles className="w-4 h-4 text-[color:var(--alpha-green-500)]" /> Browse 4 SDA categories · SIL · STA · MTA
        </div>
      </div>
    </div>
  );
}

function IntakeStage() {
  const t = useTilt(10);
  const STEPS = [
    { Icon: ClipboardCheck, label: "Referral Received", time: "Day 1" },
    { Icon: FileCheck2, label: "Suitability Checked", time: "Day 1–2" },
    { Icon: Activity, label: "Intake Plan", time: "Day 2–3" },
    { Icon: Award, label: "Support Begins", time: "Day 3+" },
  ];
  return (
    <div className="absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 hidden md:block z-30 alpha-float-a" style={{ perspective: "1200px" }}>
      <div ref={t} className="alpha-tilt alpha-glass-dark border border-white/15 rounded-2xl p-5 w-[320px] lg:w-[360px] shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <span className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><ClipboardCheck className="w-4 h-4 text-[color:var(--alpha-green-500)]" /></span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/65 font-semibold">Live intake queue</div>
              <div className="font-display text-[14px] font-semibold !text-white">Referral · #ACC-2389</div>
            </div>
          </div>
          <span className="alpha-chip !bg-[color:var(--alpha-green-600)] !text-white !border-[color:var(--alpha-green-700)] !py-0.5 !px-2 !text-[10px] uppercase tracking-[0.18em] font-semibold">Active</span>
        </div>
        <ul className="mt-4 space-y-2.5">
          {STEPS.map((s, i) => (
            <li key={s.label} className="flex items-center gap-3">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${i < 2 ? "bg-[color:var(--alpha-green-600)] text-white" : "bg-white/10 text-white/70"}`}>
                {i < 2 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <s.Icon className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold !text-white leading-tight">{s.label}</div>
                <div className="text-[10.5px] text-white/55">{s.time}</div>
              </div>
              {i < 2 && <span className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--alpha-green-500)] font-semibold">Done</span>}
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-3 border-t border-white/15 flex items-center justify-between text-[11px] text-white/65">
          <span>SLA · 1 business day</span>
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> Coordinator visible</span>
        </div>
      </div>
    </div>
  );
}

// ---------- Slide config ----------

const SLIDES = [
  {
    key: "lifestyle",
    accent: "warm",
    eyebrow: "Person-centred · NDIS Registered",
    headline: ["NDIS support for a", "genuinely good life."],
    subtext: "Person-centred SIL, SDA, nursing, daily living and community access — built around the participant.",
    primaryCta: { label: "Make a Referral", to: "/referrals" },
    secondaryCta: { label: "1300 380 692", href: "tel:1300380692", icon: Phone, surface: "hero-slide1" },
    bg: IMG.heroLifestyle,
    badges: [
      { Icon: HomeIcon, label: "SIL & SDA" },
      { Icon: Stethoscope, label: "Community Nursing" },
      { Icon: Heart, label: "Daily Living" },
      { Icon: ShieldCheck, label: "Coordination" },
    ],
    Stage: WarmStage,
  },
  {
    key: "homes",
    accent: "homes",
    eyebrow: "Our Homes · live vacancies",
    headline: ["SIL & SDA homes,", "presented like real estate."],
    subtext: "Browse safe, comfortable homes across Melbourne and Perth — designed around each participant.",
    primaryCta: { label: "View Our Homes", to: "/our-homes" },
    secondaryCta: { label: "Refer for Housing", to: "/referrals/participant" },
    bg: "/homes/coburg/1.jpg",
    badges: [
      { Icon: Sparkles, label: "Available Now" },
      { Icon: HomeIcon, label: "SIL · SDA · STA · MTA" },
      { Icon: ShieldCheck, label: "1:1 & 1:2 only" },
    ],
    Stage: HomesStage,
  },
  {
    key: "referral",
    accent: "intake",
    eyebrow: "Refer a participant",
    headline: ["Refer a participant", "with confidence."],
    subtext: "Participants, families, support coordinators, hospitals and clinicians — intake made simple, responsive and safe.",
    primaryCta: { label: "Start Referral", to: "/referrals" },
    secondaryCta: { label: "Speak to Intake", href: "tel:1300380692", icon: Phone, surface: "hero-slide3" },
    bg: IMG.heroIntake,
    badges: [
      { Icon: CheckCircle2, label: "Hospital-friendly intake" },
      { Icon: CheckCircle2, label: "Coordinator partner" },
      { Icon: CheckCircle2, label: "Plain-English agreements" },
    ],
    Stage: IntakeStage,
  },
];

// ---------- Subcomponents ----------

function ServiceBadges({ items }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((b, i) => (
        <span
          key={b.label}
          className="alpha-glass-dark border border-white/15 text-white text-[11px] uppercase tracking-[0.18em] font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 alpha-fade-up"
          style={{ animationDelay: `${0.2 + i * 0.12}s` }}
        >
          <b.Icon className="w-3 h-3 text-[color:var(--alpha-green-500)]" /> {b.label}
        </span>
      ))}
    </div>
  );
}

// ---------- Main ----------

export default function HeroSlider() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reveal, setReveal] = useState(0);
  const tick = useRef(null);

  useEffect(() => {
    if (paused) return;
    tick.current = setTimeout(() => setI((v) => (v + 1) % SLIDES.length), SLIDE_DURATION);
    return () => clearTimeout(tick.current);
  }, [i, paused]);

  useEffect(() => { setReveal((v) => v + 1); }, [i]);

  const goto = (n) => { setI(((n % SLIDES.length) + SLIDES.length) % SLIDES.length); };
  const prev = () => goto(i - 1);
  const next = () => goto(i + 1);

  const s = SLIDES[i];
  const accentBlobA = s.accent === "homes" ? "rgba(79,165,110,0.55)" : "rgba(134,89,199,0.55)";
  const accentBlobB = s.accent === "homes" ? "rgba(134,89,199,0.40)" : "rgba(79,165,110,0.40)";

  return (
    <section
      className="relative overflow-hidden text-white"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      data-testid="hero-slider"
    >
      <div className="relative h-[90vh] min-h-[640px] md:h-[94vh] md:min-h-[720px] w-full">
        {/* Slide image stack */}
        {SLIDES.map((sl, idx) => (
          <div
            key={sl.key}
            className={`absolute inset-0 transition-opacity duration-1000 ease-out ${idx === i ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
            aria-hidden={idx !== i}
          >
            <SmoothImage
              src={sl.bg}
              alt=""
              loading={idx === 0 ? "eager" : "lazy"}
              fetchPriority={idx === 0 ? "high" : "auto"}
              width={1920}
              height={1080}
              className={`absolute inset-0 w-full h-full object-cover ${idx === i ? "alpha-ken-burns" : ""}`}
            />
          </div>
        ))}

        {/* Stacked overlays — restrained, premium */}
        <div className="absolute inset-0 z-20 bg-gradient-to-b from-[#0F0625]/55 via-[#0F0625]/72 to-[#0F0625]/95" />
        <div className="absolute inset-0 z-20 bg-gradient-to-tr from-[#1B0B3D]/45 via-transparent to-[#4FA56E]/8 mix-blend-multiply" />
        <div className="alpha-grid alpha-grid-anim z-20" aria-hidden style={{ opacity: 0.55 }} />
        <div className="alpha-grain-dark z-20" aria-hidden />

        {/* Single soft cinematic vignette */}
        <div className="absolute inset-0 z-20 pointer-events-none" style={{ background: "radial-gradient(135% 90% at 50% 35%, transparent 55%, rgba(0,0,0,0.5) 100%)" }} aria-hidden />

        {/* One slow light beam, not two */}
        <div className="alpha-beam z-20" aria-hidden />

        {/* Drifting accent glows */}
        <div
          key={`glow-a-${i}`}
          className="absolute z-20 -left-24 top-12 w-[460px] h-[460px] rounded-full pointer-events-none alpha-glow-a"
          style={{ background: `radial-gradient(closest-side, ${accentBlobA}, transparent 70%)`, filter: "blur(60px)", transition: "background 1s ease" }}
          aria-hidden
        />
        <div
          key={`glow-b-${i}`}
          className="absolute z-20 -right-24 bottom-10 w-[480px] h-[480px] rounded-full pointer-events-none alpha-glow-b"
          style={{ background: `radial-gradient(closest-side, ${accentBlobB}, transparent 70%)`, filter: "blur(60px)", transition: "background 1s ease" }}
          aria-hidden
        />

        {/* Per-slide stage (desktop) */}
        <div key={`stage-${i}`}>
          <s.Stage />
        </div>

        {/* Content */}
        <div className="relative z-30 h-full alpha-container flex flex-col justify-end pb-24 md:pb-28 pt-24 md:pt-32">
          <div key={`row-${reveal}`} className="grid lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 alpha-glass-dark border border-white/15 text-white text-[10.5px] uppercase tracking-[0.28em] font-semibold rounded-full px-3 py-1.5 alpha-fade-up">
                <Sparkles className="w-3 h-3 text-[color:var(--alpha-green-500)]" /> {s.eyebrow}
              </div>
              <h1
                className="mt-5 font-display font-medium tracking-tight leading-[0.96] !text-white alpha-press alpha-fade-up alpha-delay-1"
                style={{ fontSize: "clamp(34px, 5.6vw, 78px)" }}
              >
                <span className="block">{s.headline[0]}</span>
                <span className="block alpha-serif-italic" style={{
                  background: "linear-gradient(92deg, #C8B4F0 0%, #B6E0C5 60%, #C8B4F0 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}>{s.headline[1]}</span>
              </h1>
              <p className="mt-5 text-[15.5px] md:text-lg text-white/85 max-w-2xl alpha-press alpha-fade-up alpha-delay-2">
                {s.subtext}
              </p>
              <div className="mt-7 flex flex-wrap gap-2.5 alpha-fade-up alpha-delay-3">
                <Link
                  to={s.primaryCta.to}
                  className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform shadow-2xl"
                  data-testid={`hero-slide-${s.key}-cta-primary`}
                >
                  {s.primaryCta.label} <ArrowRight className="w-4 h-4" />
                </Link>
                {s.secondaryCta.href ? (
                  <a
                    href={s.secondaryCta.href}
                    onClick={() => s.secondaryCta.href.startsWith("tel:") && trackPhoneClick("1300 380 692", s.secondaryCta.surface)}
                    className="alpha-glass-dark border border-white/30 text-white rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 hover:bg-white/15 transition-colors"
                    data-testid={`hero-slide-${s.key}-cta-secondary`}
                  >
                    {s.secondaryCta.icon && <s.secondaryCta.icon className="w-4 h-4" />}
                    {s.secondaryCta.label}
                  </a>
                ) : (
                  <Link
                    to={s.secondaryCta.to}
                    className="alpha-glass-dark border border-white/30 text-white rounded-full px-6 py-3.5 font-semibold inline-flex items-center justify-center gap-2 hover:bg-white/15 transition-colors"
                    data-testid={`hero-slide-${s.key}-cta-secondary`}
                  >
                    {s.secondaryCta.label} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
              {s.badges && (
                <div className="mt-6 alpha-fade-up alpha-delay-4">
                  <ServiceBadges items={s.badges} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Slide controls */}
        <div className="absolute z-40 inset-x-0 bottom-5 flex items-center justify-between gap-3 px-4 md:px-10">
          <div className="flex items-center gap-2">
            <button onClick={prev} aria-label="Previous slide" className="w-10 h-10 rounded-full alpha-glass-dark border border-white/20 text-white flex items-center justify-center hover:bg-white/15 transition-colors" data-testid="hero-prev"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={next} aria-label="Next slide" className="w-10 h-10 rounded-full alpha-glass-dark border border-white/20 text-white flex items-center justify-center hover:bg-white/15 transition-colors" data-testid="hero-next"><ChevronRight className="w-5 h-5" /></button>
            <button onClick={() => setPaused((v) => !v)} aria-label={paused ? "Play autoplay" : "Pause autoplay"} className="w-10 h-10 rounded-full alpha-glass-dark border border-white/20 text-white flex items-center justify-center hover:bg-white/15 transition-colors" data-testid="hero-pause">{paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}</button>
          </div>
          <div className="flex items-center gap-2" role="tablist">
            {SLIDES.map((sl, idx) => (
              <button key={sl.key} onClick={() => goto(idx)} aria-label={`Go to slide ${idx + 1}: ${sl.eyebrow}`} className="group flex items-center gap-2" data-testid={`hero-dot-${idx}`}>
                <span className={`relative h-1 rounded-full transition-all ${idx === i ? "w-16 md:w-24 bg-white/25" : "w-6 bg-white/35 group-hover:bg-white/55"}`}>
                  {idx === i && (
                    <span key={`bar-${i}-${paused ? "p" : "a"}`} className="absolute left-0 top-0 h-full rounded-full bg-white" style={{ animation: `alpha-progress ${SLIDE_DURATION}ms linear forwards`, animationPlayState: paused ? "paused" : "running" }} />
                  )}
                </span>
              </button>
            ))}
            <span className="hidden md:inline-block text-[10px] uppercase tracking-[0.22em] text-white/55 font-semibold ml-2">{String(i + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
